import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
  buildNewPlayerDocument,
  generatePlayerId,
  PLAYER_SEARCH_KEYWORD_MAX,
  PLAYER_SEARCH_KEYWORDS_MAX,
} from "../domain/identity/playerSchema";
import {
  buildSearchKeywords,
  collapsePlayersByAccount,
  normalizeDisplayName,
  playerMatchesQuery,
  validateDisplayName,
} from "../domain/identity/playerNameSearch";
import {
  CLOSED_PLAYER_DISPLAY_NAME,
  CLOSED_PLAYER_NORMALIZED_NAME,
  CLOSED_PLAYER_SEARCH_KEYWORDS,
} from "../domain/identity/accountClose";

/** @typedef {import('../domain/identity/playerTypes').PlayerRecord} PlayerRecord */
/** @typedef {import('./playerRepository.interface').PlayerSearchOptions} PlayerSearchOptions */
/** @typedef {import('./playerRepository.interface').PlayerCreateInput} PlayerCreateInput */

const COLLECTION = "players";

function playerRef(playerId) {
  return doc(db, COLLECTION, playerId);
}

/**
 * @param {import('firebase/firestore').DocumentSnapshot} snap
 * @returns {PlayerRecord | null}
 */
function fromSnap(snap) {
  if (!snap.exists()) return null;
  return { ...snap.data(), playerId: snap.id };
}

/**
 * @param {string} playerId
 * @returns {Promise<PlayerRecord | null>}
 */
export async function findById(playerId) {
  let currentId = playerId;
  for (let hop = 0; hop < 8; hop += 1) {
    const snap = await getDoc(playerRef(currentId));
    const player = fromSnap(snap);
    if (!player) return null;
    if (!player.isMerged || !player.mergedIntoPlayerId) return player;
    currentId = player.mergedIntoPlayerId;
  }
  return null;
}

/**
 * @param {PlayerSearchOptions} [options]
 * @returns {Promise<PlayerRecord[]>}
 */
export async function search(options = {}) {
  const { query: q = "", limit: max = 25, activeOnly = true } = options;
  const trimmed = (q || "").trim();
  const keywords = buildSearchKeywords(trimmed);
  const primaryToken = keywords[0] || normalizeDisplayName(trimmed);

  let baseQuery = collection(db, COLLECTION);
  const constraints = [];

  if (activeOnly) {
    constraints.push(where("isActive", "==", true));
  }
  if (primaryToken && primaryToken.length >= 2) {
    constraints.push(where("searchKeywords", "array-contains", primaryToken));
  }

  const qref =
    constraints.length > 0
      ? query(baseQuery, ...constraints, limit(Math.min(max, 50)))
      : query(baseQuery, limit(Math.min(max, 50)));

  const snap = await getDocs(qref);
  let rows = snap.docs.map((d) => fromSnap(d)).filter(Boolean);

  if (trimmed) {
    rows = rows.filter((p) => playerMatchesQuery(p, trimmed));
  }
  if (activeOnly) {
    rows = rows.filter((p) => p.isActive && !p.isMerged);
  }

  return collapsePlayersByAccount(rows).slice(0, max);
}

/**
 * @param {PlayerCreateInput} input
 * @returns {Promise<PlayerRecord>}
 */
export async function create(input) {
  const validated = validateDisplayName(input.displayName);
  if (!validated.ok) {
    throw new Error(validated.error);
  }

  const playerId = (input.playerId || generatePlayerId()).trim();
  const now = serverTimestamp();
  const payload = buildNewPlayerDocument(
    {
      displayName: validated.displayName,
      normalizedName: validated.normalizedName,
      searchKeywords: validated.searchKeywords,
      createdBy: input.createdBy,
      userId: input.userId ?? null,
    },
    playerId,
    now,
    now
  );

  await setDoc(playerRef(playerId), payload);
  const created = await findById(playerId);
  if (!created) throw new Error("Player was not created");
  return created;
}

/**
 * @param {string} playerId
 * @param {Partial<PlayerRecord>} patch
 * @returns {Promise<PlayerRecord>}
 */
export async function update(playerId, patch) {
  const existing = await getDoc(playerRef(playerId));
  if (!existing.exists()) throw new Error("Player not found");

  /** @type {Record<string, unknown>} */
  const data = { updatedAt: serverTimestamp() };

  if (patch.displayName != null) {
    const validated = validateDisplayName(patch.displayName);
    if (!validated.ok) throw new Error(validated.error);
    data.displayName = validated.displayName;
    data.normalizedName = validated.normalizedName;
    data.searchKeywords = validated.searchKeywords;
  }
  if (patch.userId !== undefined) data.userId = patch.userId;
  if (patch.isActive !== undefined) data.isActive = patch.isActive;
  if (patch.isMerged !== undefined) data.isMerged = patch.isMerged;
  if (patch.mergedIntoPlayerId !== undefined) data.mergedIntoPlayerId = patch.mergedIntoPlayerId;

  await updateDoc(playerRef(playerId), data);
  const updated = await findById(playerId);
  if (!updated) throw new Error("Player not found after update");
  return updated;
}

const PROFILE_PATCH_KEYS = new Set([
  "displayName",
  "normalizedName",
  "searchKeywords",
  "profileVersion",
  "profilePhoto",
  "photoUpdatedAt",
  "coverPhoto",
  "gender",
  "dateOfBirth",
  "preferredHand",
  "playingCategory",
  "firstName",
  "lastName",
  "city",
  "state",
  "country",
  "countryIso",
  "club",
  "preferredEvents",
  "contactEmail",
  "phoneCountryIso",
  "phoneDialCode",
  "phoneNationalNumber",
  "bio",
  "favoriteShot",
  "yearsPlaying",
  "isProfilePublic",
  "profileCompletedPercentage",
  "profileExtensions",
]);

/**
 * Profile-only update (identity lifecycle fields unchanged).
 * @param {string} playerId
 * @param {Record<string, unknown>} profilePatch
 */
export async function updateProfile(playerId, profilePatch) {
  const existing = await getDoc(playerRef(playerId));
  if (!existing.exists()) throw new Error("Player not found");

  /** @type {Record<string, unknown>} */
  const data = { updatedAt: serverTimestamp() };

  if (profilePatch.displayName != null) {
    const validated = validateDisplayName(String(profilePatch.displayName));
    if (!validated.ok) throw new Error(validated.error);
    data.displayName = validated.displayName;
    data.normalizedName = validated.normalizedName;
    data.searchKeywords = validated.searchKeywords;
  }

  Object.entries(profilePatch).forEach(([key, value]) => {
    if (key === "displayName" || key === "normalizedName" || key === "searchKeywords") return;
    if (PROFILE_PATCH_KEYS.has(key)) data[key] = value;
  });

  const existingData = existing.data();
  const nextName = data.displayName || existingData.displayName || "";
  data.searchKeywords = buildSearchKeywords(nextName, {
    club: data.club !== undefined ? data.club : existingData.club,
    country: data.country !== undefined ? data.country : existingData.country,
    state: data.state !== undefined ? data.state : existingData.state,
    city: data.city !== undefined ? data.city : existingData.city,
  });
  const loginId = normalizeDisplayName(
    profilePatch.profileExtensions?.loginId || existingData.profileExtensions?.loginId || ""
  ).slice(0, PLAYER_SEARCH_KEYWORD_MAX);
  if (loginId.length >= 2 && !data.searchKeywords.includes(loginId)) {
    data.searchKeywords = [...data.searchKeywords, loginId].slice(0, PLAYER_SEARCH_KEYWORDS_MAX);
  }

  await updateDoc(playerRef(playerId), data);
  const updated = await findById(playerId);
  if (!updated) throw new Error("Player not found after update");
  return updated;
}

/**
 * @param {{ displayName: string; createdBy: string; userId?: string | null }} input
 * @returns {Promise<PlayerRecord>}
 */
export async function findOrCreate(input) {
  const validated = validateDisplayName(input.displayName);
  if (!validated.ok) throw new Error(validated.error);

  const matches = await search({
    query: validated.displayName,
    limit: 5,
    activeOnly: true,
  });
  const exact = matches.find((p) => p.normalizedName === validated.normalizedName);
  if (exact) return exact;

  return create({
    displayName: validated.displayName,
    createdBy: input.createdBy,
    userId: input.userId ?? null,
  });
}

/**
 * @param {string} playerId
 * @param {string} userId
 * @returns {Promise<PlayerRecord>}
 */
export async function claimPlayer(playerId, userId) {
  const snap = await getDoc(playerRef(playerId));
  if (!snap.exists()) throw new Error("Player not found");
  const data = snap.data();
  if (data.isMerged) throw new Error("Cannot claim a merged player");
  if (data.userId && data.userId !== userId) {
    throw new Error("Player is already claimed by another user");
  }
  if (data.userId === userId) {
    return { ...data, playerId: snap.id };
  }
  return update(playerId, { userId });
}

/**
 * @param {string} sourcePlayerId
 * @param {string} targetPlayerId
 * @returns {Promise<{ source: PlayerRecord; target: PlayerRecord }>}
 */
export async function merge(sourcePlayerId, targetPlayerId) {
  if (sourcePlayerId === targetPlayerId) {
    throw new Error("Cannot merge a player into itself");
  }
  const [sourceSnap, targetSnap] = await Promise.all([
    getDoc(playerRef(sourcePlayerId)),
    getDoc(playerRef(targetPlayerId)),
  ]);
  if (!sourceSnap.exists() || !targetSnap.exists()) {
    throw new Error("Source or target player not found");
  }
  const source = sourceSnap.data();
  const target = targetSnap.data();
  if (source.isMerged) throw new Error("Source player is already merged");
  if (target.isMerged) throw new Error("Target player is already merged");

  const now = serverTimestamp();
  await updateDoc(playerRef(sourcePlayerId), {
    isMerged: true,
    mergedIntoPlayerId: targetPlayerId,
    isActive: false,
    updatedAt: now,
  });

  const sourceRecord = await findById(sourcePlayerId);
  const targetRecord = await findById(targetPlayerId);
  if (!sourceRecord || !targetRecord) {
    throw new Error("Merge failed");
  }
  return { source: sourceRecord, target: targetRecord };
}

/**
 * @param {string} playerId
 * @returns {Promise<PlayerRecord>}
 */
export async function deactivate(playerId) {
  return update(playerId, { isActive: false });
}

/**
 * Soft-close retain: only flips isActive (rules: isAccountCloseRetain).
 * @param {string} playerId
 * @returns {Promise<PlayerRecord>}
 */
export async function closeRetain(playerId) {
  return deactivate(playerId);
}

/**
 * Soft-close erase: anonymize live player, unlink Auth, keep playerId for history.
 * @param {string} playerId
 * @returns {Promise<PlayerRecord>}
 */
export async function closeErase(playerId) {
  const existing = await getDoc(playerRef(playerId));
  if (!existing.exists()) throw new Error("Player not found");

  await updateDoc(playerRef(playerId), {
    isActive: false,
    userId: null,
    displayName: CLOSED_PLAYER_DISPLAY_NAME,
    normalizedName: CLOSED_PLAYER_NORMALIZED_NAME,
    searchKeywords: CLOSED_PLAYER_SEARCH_KEYWORDS,
    firstName: null,
    lastName: null,
    profilePhoto: null,
    photoUpdatedAt: null,
    coverPhoto: null,
    gender: null,
    dateOfBirth: null,
    preferredHand: "",
    playingCategory: "All",
    city: null,
    state: null,
    country: null,
    countryIso: null,
    club: null,
    preferredEvents: [],
    contactEmail: null,
    phoneCountryIso: null,
    phoneDialCode: null,
    phoneNationalNumber: null,
    bio: null,
    favoriteShot: null,
    yearsPlaying: null,
    isProfilePublic: false,
    profileCompletedPercentage: 0,
    profileExtensions: {},
    updatedAt: serverTimestamp(),
  });

  const updated = await findById(playerId);
  if (!updated) throw new Error("Player not found after erase close");
  return updated;
}

/**
 * Reactivate after retain soft-close (rules: isAccountReactivate).
 * @param {string} playerId
 * @returns {Promise<PlayerRecord>}
 */
export async function reactivate(playerId) {
  return update(playerId, { isActive: true });
}

export { COLLECTION as PLAYERS_COLLECTION };
